import { cn } from "@/lib/utils";
import type { Affiliate } from "../types";
import { BadgeCheckIcon, ClockIcon, AlertTriangleIcon } from "lucide-react";
import {
  DateRangeSelector,
  type DateRangeValue,
} from "@/components/ui/date-range-selector";

export type AffiliateTab = "overview" | "links" | "referrals" | "payouts" | "settings";

type AffiliateHeaderCardProps = {
  affiliate: Affiliate;
  activeTab: AffiliateTab;
  onTabChange: (tab: AffiliateTab) => void;
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  isSticky?: boolean;
};

const tabs: { value: AffiliateTab; label: string }[] = [
  { value: "overview", label: "Overview" },
  { value: "links", label: "Links" },
  { value: "referrals", label: "Referrals" },
  { value: "payouts", label: "Payouts" },
  { value: "settings", label: "Settings" },
];

const statusConfig = {
  pending: {
    label: "Pending Approval",
    icon: ClockIcon,
    className: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  },
  active: {
    label: "Active Affiliate",
    icon: BadgeCheckIcon,
    className: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  },
  suspended: {
    label: "Suspended",
    icon: AlertTriangleIcon,
    className: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20",
  },
};

export const AffiliateHeaderCard = ({
  affiliate,
  activeTab,
  onTabChange,
  dateRange,
  onDateRangeChange,
  isSticky = false,
}: AffiliateHeaderCardProps) => {
  const status = statusConfig[affiliate.status];
  const StatusIcon = status.icon;

  return (
    <div
      className={cn(
        "rounded-lg bg-card border border-border/50 shadow-sm dark:shadow-none transition-all duration-300",
        isSticky
          ? "sticky top-0 z-50 py-2 px-3 md:px-5 backdrop-blur-sm bg-card/95 shadow-md"
          : "p-3 md:px-5 md:py-4"
      )}
    >
      {/* Mobile Layout */}
      <div className="lg:hidden space-y-4">
        {/* Title + Status */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-foreground">Affiliate Dashboard</h1>
            <p
              className={cn(
                "text-muted-foreground text-sm transition-all duration-300 overflow-hidden",
                isSticky ? "max-h-0 opacity-0 mt-0" : "max-h-10 opacity-100 mt-1"
              )}
            >
              Code: <span className="font-mono font-medium">{affiliate.refCode}</span>
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex flex-wrap items-center gap-2">
          <div className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border", status.className)}>
            <StatusIcon className="size-3.5" />
            {status.label}
          </div>
        </div>

        {/* Tabs + Date Range Selector */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg overflow-x-auto scrollbar-none max-w-full">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => onTabChange(tab.value)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-all whitespace-nowrap shrink-0",
                  activeTab === tab.value
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <DateRangeSelector value={dateRange} onChange={onDateRangeChange} />
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex items-center justify-between">
        {/* Title */}
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-bold text-foreground">Affiliate Dashboard</h1>
            <div className={cn("inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border", status.className)}>
              <StatusIcon className="size-3.5" />
              {status.label}
            </div>
          </div>
          <p
            className={cn(
              "text-muted-foreground text-sm transition-all duration-300 overflow-hidden",
              isSticky ? "max-h-0 opacity-0 mt-0" : "max-h-10 opacity-100 mt-0.5"
            )}
          >
            Earn commissions on referrals
          </p>
        </div>

        {/* Tabs (centered) */}
        <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              className={cn(
                "px-4 py-1.5 text-sm font-medium rounded-md transition-all",
                activeTab === tab.value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Date Range */}
        <DateRangeSelector value={dateRange} onChange={onDateRangeChange} />
      </div>
    </div>
  );
};
